import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { TreeItem, TreeView } from "@mui/x-tree-view";
import { Badge } from "@mui/material";
import { ReactComponent as ArrowDownOutlinedIcon } from '../../assets/icons/main/chevron-down-icon.svg';
import { ReactComponent as ArrowUpOutlinedIcon } from '../../assets/icons/main/chevron-right-icon.svg';

// Common styles
const styles = {
    checkbox: {
        display: "flex",
        alignItems: "center",
    },
    typography: {
        fontWeight: "400",
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0%",
        color: "#434D67",
    },
    badge: {
        width: "auto",
        height: "24px",
        borderRadius: "8px",
        gap: "10px",
        borderWidth: "1px",
        paddingRight: "8px",
        paddingLeft: "8px",
        background: "#537EFF",
        border: "1px solid #5C85FF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        fontSize: "12px",
    },
};

type OntologyData = {
    level_name: string;
    level_number: number;
    id: number;
    onto_manages: {
        onto_manage_title: string;
        parent_id: number;
        onto_manage_id: number;
        onto_level_number: number;
    }[];
};

type AssetData = {
    asset_name: string;
    asset_id: string;
    onto_manage_id: number;
    [key: string]: any; // Other properties
};

type AssetsTreeViewProps = {
    ontologyData: OntologyData[];
    assetsData: AssetData[];
    savedAssets?: any[];
    onAssetsSelected: (selectedAssets: { asset_name: string; asset_id: string }[]) => void;
    readonly?: boolean;
    searchTerm?: string;
};

// Build Hierarchy Function to get ontology based on assets
function buildHierarchyForAssetsOntology(ontologyData: OntologyData[], assetsData: AssetData[]) {
    /**
     * Recursively traverses the ontology data to build a hierarchical structure.
     * Each node contains its child levels, associated assets, and total assets count.
     */
    function traverse(level: any): any {
        const childLevels = ontologyData
            .filter((child) => child.onto_manages.some((m) => m.parent_id === level.onto_manage_id))
            .map((child) => {
                const manage = child.onto_manages.find((m) => m.parent_id === level.onto_manage_id);
                return traverse(manage);
            });

        const assets = assetsData.filter((asset) => asset.onto_manage_id === level.onto_manage_id);

        // Calculate total assets for this node and its children
        const total_assets = assets.length + childLevels.reduce((sum: number, child: any) => sum + child.total_assets, 0);

        return {
            onto_manage_title: level.onto_manage_title,
            onto_manage_id: level.onto_manage_id,
            assets,
            child_levels: childLevels,
            total_assets, // Add total_assets to the node
        };
    }

    return ontologyData
        .flatMap((o) => o.onto_manages)
        .filter((manage) => manage.parent_id === 0)
        .map((rootLevel) => traverse(rootLevel));
}
// Build Hierarchy Function to get all ontology and assets
function buildHierarchy(ontologyData: OntologyData[], assetsData: AssetData[]) {
    /**
     * Recursively traverses the ontology data to build a hierarchical structure.
     * Each node contains its child levels, associated assets, and total assets count.
     */
    function traverse(level: any): any {
        // Find child levels for the current level
        const childLevels = ontologyData
            .flatMap((o) => o.onto_manages)
            .filter((child) => child.parent_id === level.onto_manage_id)
            .map((child) => traverse(child));

        // Find assets associated with the current level
        const assets = assetsData.filter((asset) => asset.onto_manage_id === level.onto_manage_id);
        const total_assets = assets.length + childLevels.reduce((sum: number, child: any) => sum + child.total_assets, 0);
        return {
            onto_manage_title: level.onto_manage_title,
            onto_manage_id: level.onto_manage_id,
            assets,
            child_levels: childLevels,
            total_assets
        };
    }

    // Start with root levels (parent_id === 0)
    return ontologyData
        .flatMap((o) => o.onto_manages)
        .filter((manage) => manage.parent_id === 0)
        .map((rootLevel) => traverse(rootLevel));
}

// Initialize checkedItems state with savedAssets considered
const initializeCheckedItems = (nodes: any[], savedAssets: any[]): { [key: string]: boolean } => {
    /**
     * Recursively initializes the checked state of nodes and assets based on saved assets.
     */
    const checkedItems: { [key: string]: boolean } = {};
    const traverse = (node: any) => {
        const isNodeChecked = savedAssets.some((asset) => asset.onto_manage_id === node.onto_manage_id);
        checkedItems[node.onto_manage_id] = isNodeChecked;

        node.assets.forEach((asset: any) => {
            const isAssetChecked = savedAssets.some((saved) => saved.asset_id === asset.asset_id);
            checkedItems[asset.asset_name] = isAssetChecked;
        });

        node.child_levels.forEach((child: any) => traverse(child));
    };

    nodes.forEach((node) => traverse(node));
    return checkedItems;
};

const getParentNodeIds = (node: any, searchTerm: string, parentIds: string[] = []): string[] => {
    /**
    * Recursively collects parent node IDs for a node or asset that matches the search term.
    */
    if (node.onto_manage_title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return parentIds;
    }

    for (const child of node.child_levels) {
        const result = getParentNodeIds(child, searchTerm, [...parentIds, String(node.onto_manage_id)]);
        if (result.length > 0) {
            return result;
        }
    }

    for (const asset of node.assets) {
        if (asset.asset_name.toLowerCase().includes(searchTerm.toLowerCase())) {
            return [...parentIds, String(node.onto_manage_id)]; // Include the parent node ID
        }
    }

    return [];
};

const findMatchingNode = (node: any, searchTerm: string): any => {
    /**
     * Recursively searches for a node or asset that matches the search term.
     */
    if (node.onto_manage_title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return node;
    }

    for (const child of node.child_levels) {
        const result = findMatchingNode(child, searchTerm);
        if (result) {
            return result;
        }
    }

    for (const asset of node.assets) {
        if (asset.asset_name.toLowerCase().includes(searchTerm.toLowerCase())) {
            return node; // Return the parent node of the matching asset
        }
    }

    return null;
};

// Recursive function to get all child node IDs
const getAllChildNodeIds = (node: any): string[] => {
    /**
     * Recursively collects all child node IDs under a given node.
     */
    let childNodeIds = node.child_levels.map((child: any) => String(child.onto_manage_id));
    node.child_levels.forEach((child: any) => {
        childNodeIds = childNodeIds.concat(getAllChildNodeIds(child));
    });
    return childNodeIds;
};

// Recursive function to get all assets under a node
const getAllAssets = (node: any): { asset_name: string; asset_id: string; onto_manage_id: number }[] => {
    /**
    * Recursively collects all assets under a given node, including its child levels.
    */
    let assets = node.assets.map((asset: any) => ({
        asset_name: asset.asset_name,
        asset_id: asset.asset_id,
        onto_manage_id: node.onto_manage_id, // Include onto_manage_id
    }));

    node.child_levels.forEach((child: any) => {
        assets = assets.concat(getAllAssets(child));
    });

    return assets;
};


// Main Component
const AssetsTreeView: React.FC<AssetsTreeViewProps> = ({ ontologyData, assetsData, savedAssets, onAssetsSelected, readonly, searchTerm }) => {    
    const hierarchy = useMemo(() => buildHierarchy(ontologyData, assetsData), [ontologyData, assetsData]);
    const [expandedNodes, setExpandedNodes] = useState<string[]>(["all"]);
    const treeViewRef = useRef<HTMLDivElement>(null);
    const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>(() =>
        initializeCheckedItems(hierarchy, savedAssets || [])
    );
    const [selectedAssets, setSelectedAssets] = useState<any[]>(savedAssets || []);

    // Update selected assets when savedAssets change
    useEffect(() => {
        if (savedAssets?.length) {
            setSelectedAssets(savedAssets);
            // setCheckedItems(initializeCheckedItems(hierarchy, savedAssets));
        }
    }, [hierarchy, savedAssets]);

    // Handle search term and expand matching nodes
    useEffect(() => {
        if (searchTerm) {
            const matchingNode = hierarchy.find((node) => findMatchingNode(node, searchTerm));
            if (matchingNode) {
                const parentIds = getParentNodeIds(matchingNode, searchTerm);
                setExpandedNodes((prev) => Array.from(new Set([...prev, ...parentIds, String(matchingNode.onto_manage_id)])));

                // Delay scrolling to ensure DOM updates
                setTimeout(() => {
                    const matchingElement = treeViewRef.current?.querySelector(
                        `[data-nodeid="${matchingNode.onto_manage_id}"], [data-nodeid="${searchTerm}"]`
                    ) as HTMLElement;

                    if (matchingElement) {
                        const container = treeViewRef.current;
                        if (container) {
                            matchingElement.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                            });
                        }
                    } else {
                        console.error("Matching element not found in the DOM.");
                    }
                }, 300);
                // Adjust delay as needed
                //     setTimeout(() => {
                //     const matchingElement = treeViewRef.current?.querySelector(
                //         `[data-nodeid="${matchingNode.onto_manage_id}"], [data-nodeid="${matchingNode.asset_name}"]`
                //     ) as HTMLElement; // Cast to HTMLElement

                //     if (matchingElement) {
                //         const container = treeViewRef.current;
                //         if (container) {
                //             const containerTop = container.scrollTop;
                //             const containerHeight = container.clientHeight;
                //             const elementTop = matchingElement.offsetTop;
                //             const elementHeight = matchingElement.offsetHeight;

                //             // Check if the element is outside the visible area
                //             if (elementTop < containerTop || elementTop + elementHeight > containerTop + containerHeight) {
                //                 container.scrollTo({
                //                     top: elementTop - containerHeight / 2 + elementHeight / 2,
                //                     behavior: "smooth",
                //                 });
                //             }
                //         }
                //     }
                // }, 100);
            }
        }
    }, [searchTerm, hierarchy]);

    // Handle node toggle (expand/collapse)
    const handleNodeToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
        const lastToggledNodeId = nodeIds.find((id) => !expandedNodes.includes(id)) || expandedNodes.find((id) => !nodeIds.includes(id));

        if (lastToggledNodeId) {
            const node = hierarchy.find((n) => String(n.onto_manage_id) === lastToggledNodeId);
            if (node) {
                const childNodeIds = getAllChildNodeIds(node);

                // If collapsing, remove all child nodes from expandedNodes
                if (expandedNodes.includes(lastToggledNodeId)) {
                    setExpandedNodes((prev) => prev.filter((id) => !childNodeIds.includes(id) && id !== lastToggledNodeId));
                } else {
                    // If expanding, add the node to expandedNodes
                    setExpandedNodes(nodeIds);
                }
            } else {
                setExpandedNodes(nodeIds);
            }
        } else {
            setExpandedNodes(nodeIds);
        }
    };

    // Get all assets from the entire hierarchy
    const getAllAssetsFromHierarchy = (): { asset_name: string; asset_id: string }[] => {
        let allAssets: { asset_name: string; asset_id: string }[] = [];
        hierarchy.forEach((node) => {
            allAssets = allAssets.concat(getAllAssets(node));
        });
        return allAssets;
    };

    // Handle checkbox change
    const handleCheckboxChange = (node: any, isChecked: boolean) => {
        const updatedCheckedItems = { ...checkedItems };
        const assetsToUpdate = getAllAssets(node);

        // Update the checked state for the node and its children
        const updateCheckedState = (node: any, isChecked: boolean) => {
            updatedCheckedItems[node.onto_manage_id] = isChecked;
            node.assets.forEach((asset: any) => {
                updatedCheckedItems[asset.asset_name] = isChecked;
            });
            node.child_levels.forEach((child: any) => updateCheckedState(child, isChecked));
        };

        updateCheckedState(node, isChecked);

        // Update the selected assets list only if it changes
        const updatedSelectedAssets = isChecked
            ? [...selectedAssets, ...assetsToUpdate.filter(
                (asset) => !selectedAssets.some((selectedAsset: any) => selectedAsset.asset_id === asset.asset_id)
            )]
            : selectedAssets.filter(
                (asset: any) => !assetsToUpdate.some((updatedAsset) => updatedAsset.asset_id === asset.asset_id)
            );

        setSelectedAssets(updatedSelectedAssets);
        setCheckedItems(updatedCheckedItems);
    };

    // Handle "All" checkbox change
    const handleAllCheckboxChange = (isChecked: boolean) => {
        const updatedCheckedItems: { [key: string]: boolean } = {};
        const allAssets = getAllAssetsFromHierarchy();

        // Update all items' checked state
        hierarchy.forEach((node) => {
            const updateCheckedState = (node: any, isChecked: boolean) => {
                updatedCheckedItems[node.onto_manage_id] = isChecked;
                node.assets.forEach((asset: any) => {
                    updatedCheckedItems[asset.asset_name] = isChecked;
                });
                node.child_levels.forEach((child: any) => updateCheckedState(child, isChecked));
            };
            updateCheckedState(node, isChecked);
        });

        setCheckedItems(updatedCheckedItems);
        setSelectedAssets(isChecked ? allAssets : []);
    };

    // Notify parent component when selected assets change
    useEffect(() => {
        onAssetsSelected(selectedAssets);
    }, [selectedAssets]);

    // Render tree nodes recursively
    const renderTree = (node: any) => {
        // Helper function to determine if a node is in an indeterminate state
        const isIndeterminateState = (node: any): boolean => {
            return (
                node.child_levels.some((child: any) => checkedItems[child.onto_manage_id] || isIndeterminateState(child)) ||
                node.assets.some((asset: any) => checkedItems[asset.asset_name])
            );
        };

        // Determine if the node is checked or indeterminate
        const isChecked = !!checkedItems[node.onto_manage_id];
        const isIndeterminate =
            node.child_levels.some((child: any) => checkedItems[child.onto_manage_id] || isIndeterminateState(child)) ||
            node.assets.some((asset: any) => checkedItems[asset.asset_name]);

        return (
            <TreeItem
                key={node.onto_manage_id}
                nodeId={String(node.onto_manage_id)}
                data-nodeid={String(node.onto_manage_id)}
                label={
                    <Box sx={styles.checkbox}>
                        <Checkbox
                            disabled={readonly}
                            checked={isChecked}
                            indeterminate={isIndeterminate}
                            onChange={(e) => handleCheckboxChange(node, e.target.checked)}
                            onClick={(e) => e.stopPropagation()} // Prevent expand/collapse on checkbox click
                        />
                        {/* Display the node title with the total asset count */}
                        <Typography sx={{ ...styles.typography, display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {node.onto_manage_title}  <Badge sx={styles.badge}>{node.total_assets}</Badge>
                        </Typography>
                    </Box>
                }
            >
                {node.assets.map((asset: any,index:any) => (
                    <TreeItem
                        key={asset.asset_name+index}
                        nodeId={asset.asset_name}
                        data-nodeid={asset.asset_name}
                        label={
                            <Box sx={styles.checkbox}>
                                <Checkbox
                                    disabled={readonly}
                                    checked={!!checkedItems[asset.asset_name]}
                                    onChange={(e) => handleCheckboxChange({ assets: [asset], child_levels: [] }, e.target.checked)}
                                    onClick={(e) => e.stopPropagation()} // Prevent expand/collapse on checkbox click
                                />
                                <Typography sx={styles.typography}>{asset.asset_name}</Typography>
                            </Box>
                        }
                    />
                ))}
                {node.child_levels.map((child: any) => renderTree(child))}
            </TreeItem >
        );
    };

    return (
        <Box sx={{ width: "100%", overflowY: "scroll", height:'200px' }} ref={treeViewRef}>
            {/* "All" Checkbox as a TreeItem */}
            <TreeView
                aria-label="assets tree"
                defaultCollapseIcon={<ArrowDownOutlinedIcon style={{width:'24px', height:'24px'}} />}
                defaultExpandIcon={<ArrowUpOutlinedIcon style={{width:'24px', height:'24px'}}  />}
                expanded={expandedNodes}
                onNodeToggle={handleNodeToggle}                
                sx={{
                    overflowY: "auto",
                    p: '0 !important',
                    m: '0 !important',
                    // Remove padding/margin from all TreeItems
                    "& .MuiTreeItem-root": {
                        m:'0 !important',
                        p:'0 !important',
                    },
                    // Remove padding/margin from the content (label area)
                    "& .MuiTreeItem-content": {
                        m:'0 !important',
                        p:'0 !important',
                        minHeight: '0 !important',
                        paddingLeft:'12px !important',
                    },
                    // Remove padding/margin from the label
                    "& .MuiTreeItem-label": {
                        m: '0 !important',
                        p: '0 !important', 
                    },
                    // Remove padding/margin from the icon container
                    "& .MuiTreeItem-iconContainer": {
                        m: '0 !important',
                        p: '0 !important',
                        minWidth:'0 !important',
                        marginRight:'0px !important',
                        width: '24px !important',
                    },
                    // Remove padding/margin from the group (children)
                    "& .MuiTreeItem-group": {
                        m:'0 !important',
                        p: '0 !important',
                         ml:'36px !important',
                    },
                    // Remove padding/margin from the checkbox container
                    "& .MuiCheckbox-root": {
                        m: '0 !important',
                        p: '0 !important',
                        padding:'6px 12px 6px 12px !important',
                    },
                    // Remove padding/margin from the Typography
                    "& .MuiTypography-root": {
                        m: '0 !important',
                        p: '0 !important',
                    },
                }}
            >
                <TreeItem
                    nodeId="all"
                    label={
                        <Box sx={styles.checkbox}>
                            <Checkbox
                                disabled={readonly}
                                checked={Object.values(checkedItems).every((checked) => checked)}
                                indeterminate={
                                    Object.values(checkedItems).some((checked) => checked) &&
                                    !Object.values(checkedItems).every((checked) => checked)
                                }
                                onChange={(e) => handleAllCheckboxChange(e.target.checked)}
                                onClick={(e) => e.stopPropagation()} // Prevent expand/collapse on checkbox click
                            />
                            <Typography sx={styles.typography}>All</Typography>
                        </Box>
                    }
                >
                    {/* Render the rest of the tree */}
                    {hierarchy.map((node) => renderTree(node))}
                </TreeItem >
            </TreeView >
        </Box >
    );
};

export default React.memo(AssetsTreeView);